import React, { useState, useEffect } from "react";
import "../assets/styles/RobotMascot.scss";

type Edge = "top" | "right" | "bottom" | "left";
type RobotAction =
  | "walk"
  | "jump"
  | "flip"
  | "spin"
  | "wave"
  | "dance"
  | "bounce"
  | "shake"
  | "zoom"
  | "nod"
  | "float"
  | "roll"
  | "pulse"
  | "squat"
  | "zigzag";

const availableActions: RobotAction[] = [
  "jump",
  "flip",
  "spin",
  "wave",
  "dance",
  "bounce",
  "shake",
  "zoom",
  "nod",
  "float",
  "roll",
  "pulse",
  "squat",
  "zigzag"
];

function RobotMascot() {
  const [edge, setEdge] = useState<Edge>("top");
  const [percent, setPercent] = useState<number>(2);
  const [action, setAction] = useState<RobotAction>("walk");
  const [walkingDir, setWalkingDir] = useState<1 | -1>(1); // 1 = forward, -1 = backward

  // Trigger a random reaction from the 15 available actions
  const triggerRandomAction = () => {
    const randomAct = availableActions[Math.floor(Math.random() * availableActions.length)];
    setAction(randomAct);
    setTimeout(() => {
      setAction("walk");
    }, 1100);
  };

  // Autonomous random actions timer (every 3.5 - 6 seconds)
  useEffect(() => {
    const actionInterval = setInterval(() => {
      if (Math.random() > 0.35) {
        triggerRandomAction();
      }
    }, 4000);

    return () => clearInterval(actionInterval);
  }, []);

  // Perimeter Walking Movement with Random Direction Changes
  useEffect(() => {
    if (action !== "walk") return;

    const interval = setInterval(() => {
      setPercent((prev) => {
        const speed = 0.3 * walkingDir;

        // Check edge boundaries and randomly change direction or switch edge
        if (edge === "top") {
          if (prev >= 92 && walkingDir === 1) {
            setEdge("right");
            return 2;
          }
          if (prev <= 2 && walkingDir === -1) {
            setEdge("left");
            return 88;
          }
          if (Math.random() < 0.005) {
            setWalkingDir((d) => (d === 1 ? -1 : 1));
          }
          return prev + speed;
        } else if (edge === "right") {
          if (prev >= 88 && walkingDir === 1) {
            setEdge("bottom");
            return 92;
          }
          if (prev <= 2 && walkingDir === -1) {
            setEdge("top");
            return 92;
          }
          if (Math.random() < 0.005) {
            setWalkingDir((d) => (d === 1 ? -1 : 1));
          }
          return prev + speed;
        } else if (edge === "bottom") {
          if (prev <= 2 && walkingDir === 1) {
            setEdge("left");
            return 88;
          }
          if (prev >= 92 && walkingDir === -1) {
            setEdge("right");
            return 88;
          }
          if (Math.random() < 0.005) {
            setWalkingDir((d) => (d === 1 ? -1 : 1));
          }
          return prev - speed;
        } else {
          // edge === "left"
          if (prev <= 2 && walkingDir === 1) {
            setEdge("top");
            return 2;
          }
          if (prev >= 88 && walkingDir === -1) {
            setEdge("bottom");
            return 2;
          }
          if (Math.random() < 0.005) {
            setWalkingDir((d) => (d === 1 ? -1 : 1));
          }
          return prev - speed;
        }
      });
    }, 40);

    return () => clearInterval(interval);
  }, [edge, action, walkingDir]);

  // Viewport position styling along 4 outer edges
  const getStyle = (): React.CSSProperties => {
    switch (edge) {
      case "top":
        return { top: "8px", left: `${percent}%` };
      case "right":
        return { top: `${percent}%`, right: "12px", left: "auto" };
      case "bottom":
        return { bottom: "12px", top: "auto", left: `${percent}%` };
      case "left":
        return { top: `${percent}%`, left: "12px" };
    }
  };

  // SVG Facing Direction
  const getSvgTransform = (): string => {
    if (edge === "top") return walkingDir === 1 ? "scaleX(1)" : "scaleX(-1)";
    if (edge === "right") return walkingDir === 1 ? "rotate(90deg)" : "rotate(-90deg)";
    if (edge === "bottom") return walkingDir === 1 ? "scaleX(-1)" : "scaleX(1)";
    if (edge === "left") return walkingDir === 1 ? "rotate(-90deg)" : "rotate(90deg)";
    return "scaleX(1)";
  };

  return (
    <div className="dribbble-robot-wrapper" style={getStyle()}>
      {/* Interactive Animated SVG Robot */}
      <div
        className={`dribbble-robot action-${action}`}
        onClick={triggerRandomAction}
        onTouchStart={triggerRandomAction}
        style={{ transform: getSvgTransform() }}
      >
        <svg
          width="52"
          height="62"
          viewBox="0 0 100 110"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Antenna */}
          <g className="robot-antenna-group">
            <line x1="50" y1="20" x2="50" y2="8" stroke="#a78bfa" strokeWidth="4" strokeLinecap="round" />
            <circle
              cx="50"
              cy="6"
              r="5"
              fill={action !== "walk" ? "#fbbf24" : "#38bdf8"}
              className={`antenna-orb ${action !== "walk" ? "glowing-gold" : ""}`}
            />
          </g>

          {/* Left Arm */}
          <g className="arm left-arm">
            <rect x="18" y="52" width="10" height="24" rx="5" fill="#64748b" />
            <circle cx="23" cy="74" r="5" fill="#38bdf8" />
          </g>

          {/* Right Arm */}
          <g className="arm right-arm">
            <rect x="72" y="52" width="10" height="24" rx="5" fill="#64748b" />
            <circle cx="77" cy="74" r="5" fill="#38bdf8" />
          </g>

          {/* Torso / Body */}
          <rect x="28" y="48" width="44" height="34" rx="10" fill="url(#bodyGradient)" stroke="#7c3aed" strokeWidth="2.5" />
          {/* Chest Reactor Core */}
          <circle cx="50" cy="65" r="7" fill={action !== "walk" ? "#fbbf24" : "#38bdf8"} className="reactor-core" />
          <circle cx="50" cy="65" r="4" fill="#ffffff" />

          {/* Head */}
          <rect x="24" y="16" width="52" height="34" rx="12" fill="url(#headGradient)" stroke="#7c3aed" strokeWidth="2.5" />
          
          {/* Visor Screen */}
          <rect x="29" y="21" width="42" height="24" rx="8" fill="#090d16" stroke="#38bdf8" strokeWidth="1.5" />

          {/* Sleek Focused LED Visor Eyes */}
          <g className="normal-face">
            <circle cx="40" cy="30" r="4.5" fill={action !== "walk" ? "#fbbf24" : "#38bdf8"} className="eye-glow" />
            <circle cx="60" cy="30" r="4.5" fill={action !== "walk" ? "#fbbf24" : "#38bdf8"} className="eye-glow" />
            <circle cx="41.5" cy="28.5" r="1.5" fill="#ffffff" />
            <circle cx="61.5" cy="28.5" r="1.5" fill="#ffffff" />

            <path d="M 44 38 Q 50 41 56 38" stroke={action !== "walk" ? "#fbbf24" : "#38bdf8"} strokeWidth="2" strokeLinecap="round" fill="none" />
          </g>

          {/* Left Leg */}
          <g className="leg left-leg">
            <rect x="36" y="80" width="10" height="18" rx="4" fill="#475569" />
            <rect x="33" y="94" width="16" height="7" rx="3.5" fill="#7c3aed" />
          </g>

          {/* Right Leg */}
          <g className="leg right-leg">
            <rect x="54" y="80" width="10" height="18" rx="4" fill="#475569" />
            <rect x="51" y="94" width="16" height="7" rx="3.5" fill="#7c3aed" />
          </g>

          {/* Gradients */}
          <defs>
            <linearGradient id="headGradient" x1="24" y1="16" x2="76" y2="50" gradientUnits="userSpaceOnUse">
              <stop stopColor="#1e1b4b" />
              <stop offset="1" stopColor="#312e81" />
            </linearGradient>
            <linearGradient id="bodyGradient" x1="28" y1="48" x2="72" y2="82" gradientUnits="userSpaceOnUse">
              <stop stopColor="#1e1b4b" />
              <stop offset="1" stopColor="#4338ca" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}

export default RobotMascot;
